// @flow
import {ActionCreator, buildCatchError, createReducer} from '../../store/utils';
import {LoginSuccess, GetSessionSuccess} from '../auth/duck';
import {
  createCategoryApi,
  deleteCategoryApi,
  editCategoryApi,
  endpoints,
  listCategoriesApi,
  sortCategoriesApi,
} from './api';
import {buildRoute} from '../../http';

import {ActionsObservable, ofType, StateObservable} from 'redux-observable';
import {of} from 'rxjs';
import {
  map,
  switchMap,
  catchError,
  debounceTime,
  concatMap,
} from 'rxjs/operators';
import {uploadImageStoreApi} from '../file-storage/api';
import {createMultiFieldFormData} from '../../utils/form';
import {List} from 'immutable';
// import {arrayToOrderedMap, orderedMapToArray} from '../../utils/array';

import type {RootState} from '../index';
import type {Action} from '../../store/utils';
import type {CategoryRequest} from './api';
import type {Category, Pagination, StoredFile} from '../../entities';
import type {ApiError} from '../../http/error';
import {arrayToOrderedMap, orderedMapToArray} from '../../utils/array';
import {
  CreateSubcategorySuccess,
  DeleteSubcategorySuccess,
  EditSubcategorySuccess,
} from '../sub-categories/duck';

export type CategoryAction = Action & {
  categories?: Category[],
  sortCategoriesRequest?: {categories: string[]},
  categoryRequest?: CategoryRequest,
  error?: string | ApiError,
  reset?: boolean,
  shouldResetPagination?: boolean,
};

export type CategoryState = {
  isFetching?: boolean,
  isSaving?: boolean,
  categories?: Category[],
  fetchCategoriesError?: string,
  editCategoryError?: string,
  editCategorySuccess?: boolean,
  createCategoryError?: ApiError,
  createCategorySuccess?: boolean,
  deleteCategoryError?: string,
  deleteCategorySuccess?: boolean,
  sortCategoriesSuccess?: boolean,
  sortCategoriesError?: boolean,
  pagination?: Pagination,
  itemDeleted?: boolean,
  categoriesFilter?: Category[],
  paginationFilter?: Pagination,
};
// Search categories actions
export class FetchCategoriesSearch extends ActionCreator<CategoryAction> {}
export class FetchCategoriesSearchSuccess extends ActionCreator<CategoryAction> {}
export class FetchCategoriesSearchError extends ActionCreator<CategoryAction> {}
// Fetch categories actions
export class FetchCategories extends ActionCreator<CategoryAction> {}
export class FetchCategoriesSuccess extends ActionCreator<CategoryAction> {}
export class FetchCategoriesError extends ActionCreator<CategoryAction> {}
// Create categories actions
export class CreateCategory extends ActionCreator<CategoryAction> {}
export class CreateCategorySuccess extends ActionCreator<CategoryAction> {}
export class CreateCategoryError extends ActionCreator<CategoryAction> {}
// Edit categories actions
export class EditCategory extends ActionCreator<CategoryAction> {}
export class EditCategorySuccess extends ActionCreator<CategoryAction> {}
export class EditCategoryError extends ActionCreator<CategoryAction> {}
// Delete category actions
export class DeleteCategory extends ActionCreator<CategoryAction> {}
export class DeleteCategorySuccess extends ActionCreator<CategoryAction> {}
export class DeleteCategoryError extends ActionCreator<CategoryAction> {}
// Sort categories actions
export class SortCategories extends ActionCreator<CategoryAction> {}
export class SortCategoriesSuccess extends ActionCreator<CategoryAction> {}
export class SortCategoriesError extends ActionCreator<CategoryAction> {}
export class SortCategoriesLocal extends ActionCreator<CategoryAction> {}

export class ResetPartialState extends ActionCreator<CategoryAction> {}
export class ResetCategoriesFilter extends ActionCreator<CategoryAction> {}

export const actions = {
  fetchCategories: (payload: CategoryAction) => new FetchCategories(payload),
  fetchCategoriesSearch: (payload: CategoryAction) =>
    new FetchCategoriesSearch(payload),
  createCategory: (payload: CategoryAction) => new CreateCategory(payload),
  editCategory: (payload: CategoryAction) => new EditCategory(payload),
  deleteCategory: (payload: CategoryAction) => new DeleteCategory(payload),
  sortCategories: (payload: CategoryAction) => new SortCategories(payload),
  sortCategoriesLocal: (payload: CategoryAction) =>
    new SortCategoriesLocal(payload),
  resetState: () => new ResetPartialState(),
  resetCategoriesFilter: () => new ResetCategoriesFilter(),
};

export const initialState: CategoryState = {};

export const SIZE = 100;

export default createReducer(initialState, {
  // Search categories
  [FetchCategoriesSearch.type]: (
    state: CategoryState,
    action: CategoryAction
  ) => ({
    ...state,
    isFetching: true,
    fetchCategoriesError: undefined,
  }),
  [FetchCategoriesSearchSuccess.type]: (
    state: CategoryState,
    action: CategoryAction
  ) => ({
    ...state,
    isFetching: false,
    categoriesFilter: action.categories,
    paginationFilter: action.pagination,
    fetchCategoriesError: undefined,
    itemDeleted: false,
  }),
  [FetchCategoriesSearchError.type]: (
    state: CategoryState,
    action: CategoryAction
  ) => {
    return {
      ...state,
      isFetching: false,
      categoriesFilter: undefined,
      paginationFilter: action.pagination,
      fetchCategoriesError: undefined,
      itemDeleted: false,
    };
  },
  // Fetching products
  [FetchCategories.type]: (state: CategoryState, action: CategoryAction) => ({
    ...state,
    isFetching: true,
    fetchCategoriesError: undefined,
    pagination: action.reset ? undefined : state.pagination,
    categories: action.reset ? undefined : state.categories,
  }),
  [FetchCategoriesSuccess.type]: (
    state: CategoryState,
    action: CategoryAction
  ) => {
    let categories;
    if (action.addPage) {
      const oldCategories = arrayToOrderedMap(state.categories);
      const newCategories = arrayToOrderedMap(action.categories);
      categories = orderedMapToArray(
        state.itemDeleted ? newCategories : oldCategories.merge(newCategories)
      );
    } else {
      categories = action.categories;
    }

    return {
      ...state,
      isFetching: false,
      categories: categories,
      pagination: action.pagination,
      fetchCategoriesError: undefined,
      sortCategoriesSuccess: undefined,
      itemDeleted: false,
    };
  },
  [FetchCategoriesError.type]: (
    state: CategoryState,
    action: CategoryAction
  ) => ({
    ...state,
    isFetching: false,
    fetchCategoriesError: action.error,
    itemDeleted: false,
  }),
  // Creating category
  [CreateCategory.type]: (state: CategoryState) => ({
    ...state,
    isSaving: true,
    createCategorySuccess: undefined,
    createCategoryError: undefined,
    pagination: undefined,
  }),
  [CreateCategorySuccess.type]: (
    state: CategoryState,
    action: CategoryAction
  ) => ({
    ...state,
    categories: [...(state.categories || []), action.data],
    isSaving: false,
    createCategorySuccess: true,
    createCategoryError: undefined,
  }),
  [CreateCategoryError.type]: (
    state: CategoryState,
    action: CategoryAction
  ) => ({
    ...state,
    isSaving: false,
    createCategoryError: action.error,
  }),
  // Editing category
  [EditCategory.type]: (state: CategoryState, action: CategoryAction) => {
    const categories = state.categories;
    const categoryRequest = action.categoryRequest;
    if (!categories || !categoryRequest) return state;

    const newCategories = categories.map(category => {
      if (category.id !== categoryRequest.id) return category;
      return {...category, ...categoryRequest};
    });

    return {
      ...state,
      isSaving: true,
      editCategorySuccess: undefined,
      editCategoryError: undefined,
      categories: newCategories,
      pagination: undefined,
    };
  },
  [EditCategorySuccess.type]: (
    state: CategoryState,
    action: CategoryAction
  ) => {
    const categories = state.categories;
    const categoryResponse = action.data;

    if (!categories || !categoryResponse) return state;

    const newCategories = categories.map(category => {
      if (category.id !== categoryResponse.id) return category;
      return {...category, ...categoryResponse};
    });

    return {
      ...state,
      categories: newCategories,
      isSaving: false,
      editCategorySuccess: true,
    };
  },
  [EditCategoryError.type]: (state: CategoryState, action: CategoryAction) => ({
    ...state,
    isSaving: false,
    editCategoryError: action.error,
  }),
  // Deleting category
  [DeleteCategory.type]: (state: CategoryState) => ({
    ...state,
    isSaving: true,
    deleteCategorySuccess: undefined,
    deleteCategoryError: undefined,
    pagination: undefined,
    itemDeleted: true,
  }),
  [DeleteCategorySuccess.type]: (
    state: CategoryState,
    action: CategoryAction
  ) => {
    return {
      ...state,
      categories: state.categories
        ? state.categories.filter(c => c.id !== action.id)
        : undefined,
      isSaving: false,
      deleteCategorySuccess: true,
      deleteCategoryError: undefined,
    };
  },
  [DeleteCategoryError.type]: (
    state: CategoryState,
    action: CategoryAction
  ) => ({
    ...state,
    isSaving: false,
    deleteCategoryError: action.error,
  }),
  [SortCategoriesLocal.type]: (
    state: CategoryState,
    action: CategoryAction
  ) => {
    const {pos, item} = action.sortMovement;
    const indexElementSort = state.categories
      ? state.categories.findIndex(el => el.id === item.id)
      : -1;
    const categoriesList = List(state.categories)
      .delete(indexElementSort)
      .insert(pos, item);
    const categories = categoriesList.toArray();

    return {
      ...state,
      categories,
    };
  },
  [SortCategories.type]: (state: CategoryState) => ({
    ...state,
    isSaving: true,
    sortCategoriesSuccess: undefined,
    sortCategoriesError: undefined,
  }),
  [SortCategoriesSuccess.type]: (
    state: CategoryState,
    action: CategoryAction
  ) => ({
    ...state,
    isSaving: false,
    sortCategoriesSuccess: true,
  }),
  [SortCategoriesError.type]: (
    state: CategoryState,
    action: CategoryAction
  ) => ({
    ...state,
    isSaving: false,
    sortCategoriesError: action.error,
  }),
  [ResetPartialState.type]: (state: CategoryState) => ({
    ...initialState,
    categories: state.categories,
  }),
  [ResetCategoriesFilter.type]: (state: CategoryState) => ({
    ...state,
    categoriesFilter: undefined,
  }),
});

export function fetchCategoriesSearchEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchCategoriesSearch.type),
    debounceTime(500),
    switchMap(({queryFilter, addPage}: CategoryAction) => {
      const pagination = state$.value.categories.paginationFilter;

      const qf =
        pagination && addPage
          ? {...queryFilter, page: pagination.page + 1, size: SIZE}
          : {...queryFilter, page: 0, size: SIZE};
      return listCategoriesApi(qf).pipe(
        map(
          ({data, pagination}: {data: Category[], pagination: Pagination}) =>
            new FetchCategoriesSearchSuccess({
              categories: data,
              pagination,
              addPage: addPage,
            })
        ),
        buildCatchError(
          FetchCategoriesSearchError,
          'Error al obtener las categorias.'
        )
      );
    })
  );
}

export function fetchCategoriesEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(
      GetSessionSuccess.type,
      LoginSuccess.type,
      FetchCategories.type,
      SortCategoriesSuccess.type,
      CreateSubcategorySuccess.type,
      EditSubcategorySuccess.type,
      DeleteSubcategorySuccess.type
    ),
    debounceTime(500),
    switchMap(({queryFilters, shouldResetPagination}: CategoryAction) => {
      const pagination = state$.value.categories.pagination;
      const resetPagination =
        state$.value.categories.sortCategoriesSuccess ||
        state$.value.subCategories.updateSubcategorySuccess ||
        state$.value.subCategories.createSubcategorySuccess ||
        state$.value.subCategories.deleteSubcategorySuccess ||
        shouldResetPagination;

      const qf = !pagination
        ? {...queryFilters, page: 0, size: SIZE}
        : {
            ...queryFilters,
            page: resetPagination ? 0 : pagination.page + 1,
            size: SIZE,
          };

      return listCategoriesApi(qf).pipe(
        map(
          ({data, pagination}: {data: Category[], pagination: Pagination}) =>
            new FetchCategoriesSuccess({
              categories: data,
              pagination,
              addPage: resetPagination ? false : true,
            })
        ),
        buildCatchError(
          FetchCategoriesError,
          'Error al obtener las categorias.'
        )
      );
    })
  );
}

export function createCategoryEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(CreateCategory.type),
    switchMap(({categoryRequest}: CategoryAction) => {
      const files = [];
      if (categoryRequest.portraitImage)
        files.push(categoryRequest.portraitImage);
      if (categoryRequest.landscapeImage)
        files.push(categoryRequest.landscapeImage);

      if (files.length > 0)
        return uploadImageStoreApi(
          createMultiFieldFormData(files, undefined, {
            filePrefix: 'im/category',
          })
        ).pipe(
          map((fileImages: StoredFile[]) => [
            fileImages,
            categoryRequest,
            undefined,
          ]),
          catchError((error: ApiError) => of([undefined, undefined, error]))
        );
      else return of([[], categoryRequest, undefined]);
    }),
    concatMap(
      ([
        fileImages: StoredFile[],
        categoryRequest: CategoryRequest,
        pipeError: ApiError,
      ]) => {
        if (pipeError) return of(new CreateCategoryError({error: pipeError}));
        let categoryRq: CategoryRequest = {
          name: categoryRequest.name,
        };
        if (fileImages && fileImages.length === 1) {
          if (categoryRequest.portraitImage) {
            categoryRq.portraitImageId = fileImages[0].id;
          } else if (categoryRequest.landscapeImage) {
            categoryRq.landscapeImageId = fileImages[0].id;
          }
        } else if (fileImages && fileImages.length === 2) {
          if (fileImages && fileImages[0]) {
            categoryRq.portraitImageId = fileImages[0].id;
          }
          if (fileImages && fileImages[1]) {
            categoryRq.landscapeImageId = fileImages[1].id;
          }
        }
        return createCategoryApi(categoryRq).pipe(
          map((data: Category) => new CreateCategorySuccess({data})),
          buildCatchError(CreateCategoryError)
        );
      }
    )
  );
}

export function editCategoryEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(EditCategory.type),
    switchMap(({categoryRequest}: CategoryAction) => {
      const files = [];
      if (categoryRequest.portraitImage)
        files.push(categoryRequest.portraitImage);
      if (categoryRequest.landscapeImage)
        files.push(categoryRequest.landscapeImage);

      if (files.length > 0)
        return uploadImageStoreApi(
          createMultiFieldFormData(files, undefined, {
            filePrefix: 'im/category',
          })
        ).pipe(
          map((fileImages: StoredFile[]) => [
            fileImages,
            categoryRequest,
            undefined,
          ]),
          catchError((error: ApiError) => of([undefined, undefined, error]))
        );
      else return of([undefined, categoryRequest, undefined]);
    }),
    concatMap(
      ([
        fileImages: StoredFile[],
        categoryRequest: CategoryRequest,
        pipeError: ApiError,
      ]) => {
        if (pipeError) return of(new EditCategoryError({error: pipeError}));
        const {originalVerticalFile, originalHorizontalFile} = categoryRequest;
        let categoryRq: CategoryRequest = {
          name: categoryRequest.name,
          enabled: categoryRequest.enabled,
        };
        if (fileImages && fileImages.length === 1) {
          if (categoryRequest.portraitImage) {
            categoryRq.portraitImageId = fileImages[0].id;
          } else if (categoryRequest.landscapeImage) {
            categoryRq.landscapeImageId = fileImages[0].id;
          }
        } else if (fileImages && fileImages.length === 2) {
          if (fileImages && fileImages[0]) {
            categoryRq.portraitImageId = fileImages[0].id;
          }
          if (fileImages && fileImages[1]) {
            categoryRq.landscapeImageId = fileImages[1].id;
          }
        }

        if (originalVerticalFile) {
          if (categoryRequest.portraitImageId && !categoryRq.portraitImageId)
            categoryRq.portraitImageId = categoryRequest.portraitImageId;
        }
        if (originalHorizontalFile) {
          if (categoryRequest.landscapeImageId && !categoryRq.landscapeImageId)
            categoryRq.landscapeImageId = categoryRequest.landscapeImageId;
        }

        return editCategoryApi(categoryRq, {
          route: buildRoute(endpoints.EDIT_CATEGORY, [categoryRequest.id]),
        }).pipe(
          map((data: Category) => new EditCategorySuccess({data})),
          buildCatchError(EditCategoryError, 'Error al editar la categoria.')
        );
      }
    )
  );
}

export function deleteCategoryEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(DeleteCategory.type),
    switchMap(({categoryRequest}: CategoryAction) =>
      deleteCategoryApi({
        route: buildRoute(endpoints.DELETE_CATEGORY, [categoryRequest.id]),
      }).pipe(
        map(() => new DeleteCategorySuccess({id: categoryRequest.id})),
        buildCatchError(DeleteCategoryError, 'Error al borrar la categoría.')
      )
    )
  );
}

export function sortCategoriesEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SortCategories.type),
    switchMap(({sortCategoriesRequest}: CategoryAction) => {
      const {init, end} = sortCategoriesRequest;
      return sortCategoriesApi({
        targetProductCategoryId: end.id,
        originProductCategoryId: init.id,
      }).pipe(
        map(
          (categories: Category[]) => new SortCategoriesSuccess({categories})
        ),
        buildCatchError(SortCategoriesError, 'Error al ordenar las categorias.')
      );
    })
  );
}
